<template>
  <div class="restaurant-list-page">
    <!-- Load restaurants on mount -->
    <VBusHelper :emitOnMounted="$constants.EVENTS.RESTAURANTS.LIST" />

    <!-- Header -->
    <div class="header">
      <h1>Restaurants</h1>
      <p class="subtitle">
        Découvrez les meilleurs restaurants près de chez vous
      </p>
    </div>

    <!-- Search Bar -->
    <div class="search-bar">
      <input
        type="text"
        placeholder="Rechercher un restaurant..."
        class="search-input"
        v-model="searchQuery"
        @keyup.enter="handleSearch"
      />
      <button
        class="search-button"
        @click="handleSearch"
        :disabled="$stores.restaurants.state.search.loading"
      >
        Rechercher
      </button>
    </div>

    <!-- Filters -->
    <div class="filters">
      <button
        v-for="cuisine in cuisines"
        :key="cuisine"
        class="filter-button"
        :class="{ active: isFilterActive('cuisine', cuisine) }"
        @click="toggleFilter('cuisine', cuisine)"
      >
        {{ cuisine }}
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="$stores.restaurants.state.list.loading" class="loading">
      <div class="spinner"></div>
      <p>Chargement des restaurants...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="$stores.restaurants.state.list.error" class="error">
      <p>{{ $stores.restaurants.state.list.error }}</p>
      <button @click="retry" class="retry-button">Réessayer</button>
    </div>

    <!-- Empty State -->
    <div v-else-if="restaurantList.length === 0" class="empty">
      <p>Aucun restaurant trouvé</p>
    </div>

    <!-- Restaurant List -->
    <div v-else class="px-6 grid grid-cols-1 gap-6">
      <RestaurantCard
        v-for="restaurant in restaurantList"
        :key="restaurant.id"
        :item="restaurant"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import RestaurantCard from '@features/home/components/RestaurantCard.vue';

const searchQuery = ref('');
const cuisines = [
  'Tous',
  'Africain',
  'Asiatique',
  'Européen',
  'Fast-food',
  'Pizzeria',
  'Dessert'
];
const activeFilters = ref({
  cuisine: []
});

const restaurantList = computed(() => {
  return window.$stores?.restaurants?.state?.list?.items || [];
});

const isFilterActive = (type, value) => {
  if (value === 'Tous') return activeFilters.value.cuisine.length === 0;
  return activeFilters.value.cuisine.includes(value);
};

const toggleFilter = (type, value) => {
  if (value === 'Tous') {
    activeFilters.value.cuisine = [];
  } else {
    const index = activeFilters.value.cuisine.indexOf(value);
    if (index > -1) {
      activeFilters.value.cuisine.splice(index, 1);
    } else {
      activeFilters.value.cuisine.push(value);
    }
  }
};

const handleSearch = () => {
  window.$bus?.emit(window.$constants?.EVENTS?.RESTAURANTS?.SEARCH, {
    searchQuery: searchQuery.value,
    filters: activeFilters.value
  });
};

const retry = () => {
  window.$bus?.emit(window.$constants?.EVENTS?.RESTAURANTS?.LIST);
};

const goToDetail = (id) => {
  window.$router?.push(`/restaurants/${id}`);
};
</script>

<style scoped>
.restaurant-list-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.header {
  text-align: center;
  margin-bottom: 30px;
}

.header h1 {
  font-size: 2em;
  font-weight: bold;
  color: #1a1a1a;
  margin-bottom: 8px;
}

.subtitle {
  color: #666;
  font-size: 1em;
}

.search-bar {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
}

.search-input {
  flex: 1;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1em;
}

.search-button {
  padding: 12px 24px;
  background: #f59e0b;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
}

.search-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.filters {
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.filter-button {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 20px;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
}

.filter-button:hover {
  border-color: #f59e0b;
}

.filter-button.active {
  background: #f59e0b;
  color: white;
  border-color: #f59e0b;
}

.loading,
.error,
.empty {
  text-align: center;
  padding: 40px 20px;
}

.spinner {
  border: 3px solid #f3f3f3;
  border-top: 3px solid #f59e0b;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.error {
  color: #dc2626;
}

.retry-button {
  margin-top: 16px;
  padding: 10px 20px;
  background: #f59e0b;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
}

.restaurant-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.restaurant-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition:
    transform 0.2s,
    box-shadow 0.2s;
}

.restaurant-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.restaurant-image {
  position: relative;
  height: 200px;
}

.restaurant-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.featured-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  background: #f59e0b;
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.85em;
  font-weight: 600;
}

.verified-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  background: #10b981;
  color: white;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.restaurant-info {
  padding: 16px;
}

.restaurant-name {
  font-size: 1.25em;
  font-weight: 600;
  margin-bottom: 8px;
  color: #1a1a1a;
}

.restaurant-description {
  color: #666;
  font-size: 0.9em;
  margin-bottom: 12px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.restaurant-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
  font-size: 0.9em;
}

.rating {
  display: flex;
  align-items: center;
  gap: 4px;
}

.star {
  color: #f59e0b;
}

.review-count {
  color: #666;
}

.price-range {
  color: #666;
}

.delivery-info {
  color: #666;
}

.delivery-fee {
  font-weight: 500;
}

.restaurant-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag {
  background: #f3f4f6;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.85em;
  color: #4b5563;
}

.status {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.85em;
  font-weight: 500;
}

.status.open {
  background: #d1fae5;
  color: #065f46;
}

.status.closed {
  background: #fee2e2;
  color: #991b1b;
}
</style>
